import { supabase } from "@/lib/supabase";
import {
  assertCanonicalIndividualComplianceRequirementKey,
  parseCanonicalIndividualComplianceRead,
  type CanonicalIndividualComplianceRead,
  type CanonicalIndividualComplianceRequirementKey,
  type ManagedCanonicalIndividualComplianceRead,
} from "@/data/contractor/compliance/canonicalIndividualCompliance";

const READ_FUNCTION = "individual-compliance-read";
const EVIDENCE_FUNCTION = "individual-compliance-evidence";
const EVIDENCE_READ_FUNCTION = "individual-compliance-evidence-read-url";

type FunctionResult = Readonly<{
  data: unknown;
  error: unknown;
}>;

export type IndividualComplianceFunctionInvoker = (
  functionName: string,
  options: Readonly<{ body: Readonly<Record<string, unknown>> }>,
) => Promise<FunctionResult>;

export type CanonicalIndividualComplianceUploadRequest = Readonly<{
  uploadIntentId: string;
  requirementKey: CanonicalIndividualComplianceRequirementKey;
  uploadUrl: string;
  token: string;
  expiresAt: string;
  maxByteSize: number;
  allowedMimeTypes: readonly string[];
}>;

export type CanonicalIndividualComplianceFinalizeResult = Readonly<{
  evidenceId: string;
  recordId: string;
  snapshotId: string;
  requirementId: string;
  requirementKey: CanonicalIndividualComplianceRequirementKey;
  docType: string;
  originalFileName: string;
  mimeType: string;
  byteSize: number;
  submittedAt: string;
  submittedBy: string | null;
}>;

export type CanonicalIndividualCompliancePreview = Readonly<{
  signedUrl: string;
  expiresAt: string;
  fileName: string;
  mimeType: string;
  byteSize: number;
}>;

export type CanonicalIndividualComplianceUploadCompletion = Readonly<{
  finalization: CanonicalIndividualComplianceFinalizeResult;
  compliance: CanonicalIndividualComplianceRead;
}>;

export class CanonicalIndividualComplianceClientError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = "CanonicalIndividualComplianceClientError";
  }
}

export function isCanonicalIndividualComplianceEnabled(
  rawValue: unknown = import.meta.env
    .VITE_CANONICAL_INDIVIDUAL_COMPLIANCE_ENABLED,
): boolean {
  return rawValue === "true";
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function requireString(value: unknown, code: string): string {
  const normalized = typeof value === "string" ? value.trim() : "";
  if (!normalized) throw new CanonicalIndividualComplianceClientError(code, code);
  return normalized;
}

function requirePositiveNumber(value: unknown, code: string): number {
  const normalized = Number(value);
  if (!Number.isFinite(normalized) || normalized <= 0) {
    throw new CanonicalIndividualComplianceClientError(code, code);
  }
  return normalized;
}

function throwFunctionError(
  data: unknown,
  error: unknown,
  fallback: string,
): never {
  const response = asRecord(data);
  const responseError =
    typeof response.error === "string" && response.error.trim()
      ? response.error
      : null;
  const transportMessage =
    error && typeof error === "object" && "message" in error
      ? String((error as { message?: unknown }).message ?? "")
      : "";
  const message = responseError ?? transportMessage.trim() ?? fallback;
  throw new CanonicalIndividualComplianceClientError(
    message || fallback,
    typeof response.code === "string" ? response.code : undefined,
  );
}

function parseUploadRequest(
  value: unknown,
  requestedRequirementKey: CanonicalIndividualComplianceRequirementKey,
): CanonicalIndividualComplianceUploadRequest {
  const data = asRecord(value);
  const requirementKey = assertCanonicalIndividualComplianceRequirementKey(
    data.requirementKey,
  );
  if (requirementKey !== requestedRequirementKey) {
    throw new CanonicalIndividualComplianceClientError(
      "canonical_individual_compliance_upload_requirement_mismatch",
      "canonical_individual_compliance_upload_requirement_mismatch",
    );
  }
  const allowedMimeTypes = Array.isArray(data.allowedMimeTypes)
    ? data.allowedMimeTypes.map((mimeType) =>
        requireString(
          mimeType,
          "canonical_individual_compliance_upload_contract_invalid",
        ))
    : [];
  if (allowedMimeTypes.length === 0) {
    throw new CanonicalIndividualComplianceClientError(
      "canonical_individual_compliance_upload_contract_invalid",
      "canonical_individual_compliance_upload_contract_invalid",
    );
  }
  return Object.freeze({
    uploadIntentId: requireString(
      data.uploadIntentId,
      "canonical_individual_compliance_upload_contract_invalid",
    ),
    requirementKey,
    uploadUrl: requireString(
      data.uploadUrl,
      "canonical_individual_compliance_upload_contract_invalid",
    ),
    token: requireString(
      data.token,
      "canonical_individual_compliance_upload_contract_invalid",
    ),
    expiresAt: requireString(
      data.expiresAt,
      "canonical_individual_compliance_upload_contract_invalid",
    ),
    maxByteSize: requirePositiveNumber(
      data.maxByteSize,
      "canonical_individual_compliance_upload_contract_invalid",
    ),
    allowedMimeTypes: Object.freeze(allowedMimeTypes),
  });
}

function parseFinalizeResult(
  value: unknown,
): CanonicalIndividualComplianceFinalizeResult {
  const data = asRecord(value);
  return Object.freeze({
    evidenceId: requireString(
      data.evidenceId,
      "canonical_individual_compliance_finalize_contract_invalid",
    ),
    recordId: requireString(
      data.recordId,
      "canonical_individual_compliance_finalize_contract_invalid",
    ),
    snapshotId: requireString(
      data.snapshotId,
      "canonical_individual_compliance_finalize_contract_invalid",
    ),
    requirementId: requireString(
      data.requirementId,
      "canonical_individual_compliance_finalize_contract_invalid",
    ),
    requirementKey: assertCanonicalIndividualComplianceRequirementKey(
      data.requirementKey,
    ),
    docType: requireString(
      data.docType,
      "canonical_individual_compliance_finalize_contract_invalid",
    ),
    originalFileName: requireString(
      data.originalFileName,
      "canonical_individual_compliance_finalize_contract_invalid",
    ),
    mimeType: requireString(
      data.mimeType,
      "canonical_individual_compliance_finalize_contract_invalid",
    ),
    byteSize: requirePositiveNumber(
      data.byteSize,
      "canonical_individual_compliance_finalize_contract_invalid",
    ),
    submittedAt: requireString(
      data.submittedAt,
      "canonical_individual_compliance_finalize_contract_invalid",
    ),
    submittedBy:
      data.submittedBy === null
        ? null
        : requireString(
            data.submittedBy,
            "canonical_individual_compliance_finalize_contract_invalid",
          ),
  });
}

function parsePreview(value: unknown): CanonicalIndividualCompliancePreview {
  const data = asRecord(value);
  return Object.freeze({
    signedUrl: requireString(
      data.signedUrl,
      "canonical_individual_compliance_preview_contract_invalid",
    ),
    expiresAt: requireString(
      data.expiresAt,
      "canonical_individual_compliance_preview_contract_invalid",
    ),
    fileName: requireString(
      data.fileName,
      "canonical_individual_compliance_preview_contract_invalid",
    ),
    mimeType: requireString(
      data.mimeType,
      "canonical_individual_compliance_preview_contract_invalid",
    ),
    byteSize: requirePositiveNumber(
      data.byteSize,
      "canonical_individual_compliance_preview_contract_invalid",
    ),
  });
}

function defaultInvoker(
  functionName: string,
  options: Readonly<{ body: Readonly<Record<string, unknown>> }>,
): Promise<FunctionResult> {
  return supabase.functions.invoke(functionName, options);
}

export function createCanonicalIndividualComplianceClient(dependencies?: {
  invoke?: IndividualComplianceFunctionInvoker;
  uploadBytes?: (
    request: CanonicalIndividualComplianceUploadRequest,
    file: File,
  ) => Promise<void>;
  enabled?: () => boolean;
}) {
  const invoke = dependencies?.invoke ?? defaultInvoker;
  const enabled =
    dependencies?.enabled ?? isCanonicalIndividualComplianceEnabled;
  const uploadBytes =
    dependencies?.uploadBytes
    ?? (async (
      request: CanonicalIndividualComplianceUploadRequest,
      file: File,
    ) => {
      const response = await fetch(request.uploadUrl, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${request.token}`,
          "Content-Type": file.type,
          "x-upsert": "false",
        },
        body: file,
      });
      if (!response.ok) {
        const detail = await response.text().catch(() => "");
        throw new CanonicalIndividualComplianceClientError(
          detail
            ? `canonical_individual_compliance_upload_bytes_failed: ${detail}`
            : "canonical_individual_compliance_upload_bytes_failed",
          "canonical_individual_compliance_upload_bytes_failed",
        );
      }
    });

  function assertEnabled(): void {
    if (!enabled()) {
      throw new CanonicalIndividualComplianceClientError(
        "canonical_individual_compliance_disabled",
        "canonical_individual_compliance_disabled",
      );
    }
  }

  async function call(
    functionName: string,
    body: Readonly<Record<string, unknown>>,
    fallback: string,
  ): Promise<unknown> {
    assertEnabled();
    const { data, error } = await invoke(functionName, { body });
    if (error || asRecord(data).error) {
      throwFunctionError(data, error, fallback);
    }
    return data;
  }

  async function getSelf(
    selectedContractorId: string,
  ): Promise<CanonicalIndividualComplianceRead> {
    const contractorId = requireString(
      selectedContractorId,
      "canonical_individual_compliance_workspace_required",
    );
    const data = asRecord(
      await call(
        READ_FUNCTION,
        { contractorId },
        "canonical_individual_compliance_read_failed",
      ),
    );
    return parseCanonicalIndividualComplianceRead(data.compliance);
  }

  async function getManagedCrew(
    selectedContractorId: string,
    personUserId?: string,
  ): Promise<readonly ManagedCanonicalIndividualComplianceRead[]> {
    const contractorId = requireString(
      selectedContractorId,
      "canonical_individual_compliance_workspace_required",
    );
    const targetUserId = personUserId
      ? requireString(
          personUserId,
          "canonical_individual_compliance_manager_target_required",
        )
      : null;
    const data = asRecord(
      await call(
        READ_FUNCTION,
        {
          operation: targetUserId ? "manager_read" : "manager_batch",
          contractorId,
          ...(targetUserId ? { personUserId: targetUserId } : {}),
        },
        "canonical_individual_compliance_manager_read_failed",
      ),
    );
    if (!Array.isArray(data.members)) {
      throw new CanonicalIndividualComplianceClientError(
        "canonical_individual_compliance_read_contract_invalid",
        "canonical_individual_compliance_read_contract_invalid",
      );
    }
    const seen = new Set<string>();
    return Object.freeze(
      data.members.map((value): ManagedCanonicalIndividualComplianceRead => {
        const member = asRecord(value);
        const userId = requireString(
          member.userId,
          "canonical_individual_compliance_read_contract_invalid",
        );
        if (member.membershipStatus !== "active" || seen.has(userId)) {
          throw new CanonicalIndividualComplianceClientError(
            "canonical_individual_compliance_read_contract_invalid",
            "canonical_individual_compliance_read_contract_invalid",
          );
        }
        seen.add(userId);
        return Object.freeze({
          userId,
          membershipStatus: "active",
          compliance: parseCanonicalIndividualComplianceRead(
            member.compliance,
          ),
        });
      }),
    );
  }

  async function requestUpload(args: {
    requirementKey: CanonicalIndividualComplianceRequirementKey;
    fileName: string;
    mimeType: string | null;
    byteSize: number;
  }): Promise<CanonicalIndividualComplianceUploadRequest> {
    const requirementKey = assertCanonicalIndividualComplianceRequirementKey(
      args.requirementKey,
    );
    const fileName = requireString(
      args.fileName,
      "canonical_individual_compliance_file_name_required",
    );
    const byteSize = requirePositiveNumber(
      args.byteSize,
      "canonical_individual_compliance_file_size_invalid",
    );
    const data = await call(
      EVIDENCE_FUNCTION,
      {
        operation: "request_upload",
        requirementKey,
        fileName,
        mimeType: args.mimeType,
        byteSize,
      },
      "canonical_individual_compliance_upload_request_failed",
    );
    return parseUploadRequest(data, requirementKey);
  }

  async function putSignedUpload(
    request: CanonicalIndividualComplianceUploadRequest,
    file: File,
  ): Promise<void> {
    assertEnabled();
    await uploadBytes(request, file);
  }

  async function finalizeUpload(
    uploadIntentId: string,
  ): Promise<CanonicalIndividualComplianceFinalizeResult> {
    const intentId = requireString(
      uploadIntentId,
      "canonical_individual_compliance_upload_intent_required",
    );
    return parseFinalizeResult(
      await call(
        EVIDENCE_FUNCTION,
        { operation: "finalize_upload", uploadIntentId: intentId },
        "canonical_individual_compliance_upload_finalize_failed",
      ),
    );
  }

  async function getSignedPreview(args: {
    evidenceId: string;
    selectedContractorId?: string | null;
    expiresInSeconds?: number;
  }): Promise<CanonicalIndividualCompliancePreview> {
    const evidenceId = requireString(
      args.evidenceId,
      "canonical_individual_compliance_evidence_id_required",
    );
    const selectedContractorId = args.selectedContractorId
      ? requireString(
          args.selectedContractorId,
          "canonical_individual_compliance_workspace_required",
        )
      : null;
    return parsePreview(
      await call(
        EVIDENCE_READ_FUNCTION,
        {
          evidenceId,
          ...(selectedContractorId
            ? { contractorId: selectedContractorId }
            : {}),
          ...(args.expiresInSeconds
            ? { expiresInSeconds: args.expiresInSeconds }
            : {}),
        },
        "canonical_individual_compliance_preview_failed",
      ),
    );
  }

  async function uploadAndRefresh(args: {
    requirementKey: CanonicalIndividualComplianceRequirementKey;
    file: File;
    refresh: () => Promise<CanonicalIndividualComplianceRead>;
  }): Promise<CanonicalIndividualComplianceUploadCompletion> {
    const request = await requestUpload({
      requirementKey: args.requirementKey,
      fileName: args.file.name,
      mimeType: args.file.type || null,
      byteSize: args.file.size,
    });
    await putSignedUpload(request, args.file);
    const finalization = await finalizeUpload(request.uploadIntentId);
    const compliance = await args.refresh();
    return Object.freeze({ finalization, compliance });
  }

  return Object.freeze({
    getSelf,
    getManagedCrew,
    requestUpload,
    putSignedUpload,
    finalizeUpload,
    getSignedPreview,
    uploadAndRefresh,
  });
}

export const canonicalIndividualComplianceClient =
  createCanonicalIndividualComplianceClient();
